import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { homesiteInspect } from '../../../constants/routes';

function authHeader() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

function nowDatetimeLocal() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function HomesiteHome() {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [locations, setLocations] = useState([]);
  const [propertyId, setPropertyId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [residentId, setResidentId] = useState('');
  const [inspectorName, setInspectorName] = useState('');
  const [inspectedAtLocal, setInspectedAtLocal] = useState(() => nowDatetimeLocal());
  const [warningNumber, setWarningNumber] = useState('none');
  const [dueDateCorrections, setDueDateCorrections] = useState('');
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');

  const selectedLocation = useMemo(
    () => locations.find((l) => String(l.id) === String(locationId)) || null,
    [locations, locationId]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setError('');
      setLoading(true);
      try {
        const [pRes, tRes] = await Promise.all([
          axios.get('/api/homesite/picker/properties', { headers: authHeader() }),
          axios.get('/api/homesite/templates', { headers: authHeader() }),
        ]);
        if (cancelled) return;
        setProperties(pRes.data);
        setTemplates(tRes.data);
        if (tRes.data.length === 1) setTemplateId(String(tRes.data[0].id));
        else {
          const homesite = tRes.data.find((t) => t.slug === 'homesite-inspections');
          if (homesite) setTemplateId(String(homesite.id));
        }
        if (pRes.data.length === 1) setPropertyId(String(pRes.data[0].id));
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.error || e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!propertyId) {
      setLocations([]);
      setLocationId('');
      setResidentId('');
      return;
    }
    (async () => {
      try {
        const res = await axios.get('/api/homesite/picker/locations', {
          headers: authHeader(),
          params: { propertyId },
        });
        if (cancelled) return;
        const rows = res.data || [];
        setLocations(rows);
        if (rows.length === 1) setLocationId(String(rows[0].id));
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.error || e.message || 'Failed to load locations');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  useEffect(() => {
    const residents = selectedLocation?.residents || [];
    if (residents.length === 1) setResidentId(String(residents[0].id));
    else if (!residents.some((r) => String(r.id) === String(residentId))) setResidentId('');
  }, [selectedLocation, residentId]);

  const startInspection = async () => {
    if (!propertyId || !templateId || !locationId || !inspectorName || !inspectedAtLocal) {
      setError('Select property/location/checklist and enter inspector/date.');
      return;
    }
    setStarting(true);
    setError('');
    try {
      const res = await axios.post(
        '/api/homesite/inspections',
        {
          propertyId: Number(propertyId),
          templateId: Number(templateId),
          locationId: Number(locationId),
          residentId: residentId ? Number(residentId) : null,
          inspectorName,
          inspectedAt: new Date(inspectedAtLocal).toISOString(),
          warningNumber,
          dueDateCorrections: dueDateCorrections || null,
        },
        { headers: authHeader() }
      );
      navigate(homesiteInspect(res.data.id));
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Could not start inspection');
    } finally {
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <div className="app-loading text-muted" role="status">
        <span className="spinner-border spinner-border-sm" aria-hidden="true" />
        <span>Loading…</span>
      </div>
    );
  }

  const residents = selectedLocation?.residents || [];

  return (
    <div className="card app-card" style={{ maxWidth: 720 }}>
      <div className="card-body">
        <h2 className="h5 card-title">Start homesite inspection</h2>
        {error && <div className="alert alert-danger py-2">{error}</div>}
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label">Community</label>
            <select className="form-select" value={propertyId} onChange={(e) => setPropertyId(e.target.value)}>
              <option value="">Select community...</option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-6">
            <label className="form-label">Checklist</label>
            <select className="form-select" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
              <option value="">Select checklist...</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-6">
            <label className="form-label">Inspector</label>
            <input
              type="text"
              className="form-control"
              value={inspectorName}
              onChange={(e) => setInspectorName(e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Date & time</label>
            <input
              type="datetime-local"
              className="form-control"
              value={inspectedAtLocal}
              onChange={(e) => setInspectedAtLocal(e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Location</label>
            <select className="form-select" value={locationId} onChange={(e) => setLocationId(e.target.value)}>
              <option value="">Select location...</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.site_name || `Location ${l.id}`}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-6">
            <label className="form-label">Resident</label>
            <select className="form-select" value={residentId} onChange={(e) => setResidentId(e.target.value)}>
              <option value="">No resident selected</option>
              {residents.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-6">
            <label className="form-label">Warning Number</label>
            <select className="form-select" value={warningNumber} onChange={(e) => setWarningNumber(e.target.value)}>
              <option value="none">None</option>
              <option value="first">1st</option>
              <option value="second">2nd</option>
            </select>
          </div>
          <div className="col-md-6">
            <label className="form-label">Due Date of Corrections</label>
            <input
              type="date"
              className="form-control"
              value={dueDateCorrections}
              onChange={(e) => setDueDateCorrections(e.target.value)}
            />
          </div>
        </div>
        <button
          type="button"
          className="btn btn-primary mt-3"
          disabled={starting}
          onClick={startInspection}
        >
          {starting ? 'Starting...' : 'Start inspection'}
        </button>
      </div>
    </div>
  );
}

export default HomesiteHome;
